import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProfiles, updateProfile, deleteAccount } from '../api';
import { useToast } from '../App';
import { useAuth } from '../context/AuthContext';
import { User, CreditCard, MessageSquare, Monitor, Sun, Moon, ShieldCheck, X, Image, Globe, Phone } from '../components/Icons';
import { CustomSelect } from '../components/CustomSelect';
import './Settings.css';

const PROFESSION_OPTIONS = [
  { value: 'Web Developer',                  label: 'Web Developer' },
  { value: 'Mobile Developer',               label: 'Mobile Developer' },
  { value: 'UI/UX Designer',                 label: 'UI/UX Designer' },
  { value: 'Graphic Designer',               label: 'Graphic Designer' },
  { value: 'Copywriter / Content Writer',    label: 'Copywriter / Content Writer' },
  { value: 'Video Editor / Motion Designer', label: 'Video Editor / Motion Designer' },
  { value: 'Marketing Consultant',           label: 'Marketing Consultant' },
  { value: 'Business Consultant',            label: 'Business Consultant' },
  { value: 'Project Manager',                label: 'Project Manager' },
  { value: 'Photographer / Videographer',    label: 'Photographer / Videographer' },
  { value: 'Other',                          label: 'Other' },
];

const TONES = [
  { value: 'professional', label: 'Professional', desc: 'Clear, confident, business-appropriate' },
  { value: 'firm', label: 'Firm', desc: 'Direct and assertive, no-nonsense but respectful' },
  { value: 'friendly', label: 'Friendly', desc: 'Warm, approachable, collaborative' },
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'AUD', 'CAD', 'SGD', 'AED'];

export default function Settings() {
  const navigate  = useNavigate();
  const { addToast } = useToast();
  const { user, planMeta, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  // Delete account modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteInput, setDeleteInput]         = useState('');
  const [deleting, setDeleting]               = useState(false);

  const canCustomTone = planMeta?.customTone === true;

  const [form, setForm] = useState({
    email: user?.email || '',
    name: '',
    profession: '',
    hourly_rate: 50,
    currency: 'USD',
    preferred_tone: 'professional',
    theme: 'system',
    branding_company_name: '',
    branding_logo_url: '',
    branding_address: '',
    branding_phone: '',
    branding_website: '',
    branding_tagline: '',
  });

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const profiles = await getProfiles();
      if (profiles.length > 0) {
        const p = profiles[0];
        setForm({
          email: p.email || user?.email || '',
          name: p.name || '',
          profession: p.profession || '',
          hourly_rate: p.hourly_rate || 50,
          currency: p.currency || 'USD',
          preferred_tone: p.preferred_tone || 'professional',
          theme: p.theme || 'system',
          branding_company_name: p.branding_company_name || '',
          branding_logo_url:     p.branding_logo_url     || '',
          branding_address:      p.branding_address      || '',
          branding_phone:        p.branding_phone        || '',
          branding_website:      p.branding_website      || '',
          branding_tagline:      p.branding_tagline      || '',
        });
      }
    } catch {
      addToast('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [user?.email, addToast]);  // only what loadProfile reads from outside

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);

    const toneToSave = canCustomTone ? form.preferred_tone : 'professional';

    try {
      await updateProfile(null, { ...form, preferred_tone: toneToSave });

      const theme = form.theme;
      if (theme === 'system') {
        const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
      } else {
        document.documentElement.setAttribute('data-theme', theme);
      }

      addToast('Settings saved!', 'success');
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteAccount() {
    if (deleteInput !== 'DELETE') return;
    setDeleting(true);
    try {
      await deleteAccount();
      await logout();
      navigate('/');
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to delete account. Please try again.', 'error');
      setDeleting(false);
    }
  }

  function handleChange(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  return (
    <div className="settings-page animate-fade-in">
      <div className="page-header">
        <div>
          <h1>Settings</h1>
          <p className="page-title-sub">Configure your profile and application preferences</p>
        </div>
        {/* Plan badge */}
        <div className="settings-plan-badge" style={{ '--plan-color': planMeta?.color || 'var(--accent)' }}>
          <ShieldCheck size={14} />
          {planMeta?.label || 'Starter'} Plan
        </div>
      </div>

      {loading ? (
        <div className="skeleton" style={{ height: 400, borderRadius: 'var(--radius-lg)' }} />
      ) : (
        <form onSubmit={handleSave} className="settings-form">

          {/* Theme Section */}
          <div className="settings-section card">
            <div className="settings-section-header">
              <h3><Monitor size={18} /> App Appearance</h3>
              <p>Choose your preferred theme across the dashboard</p>
            </div>
            <div className="theme-options" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
              {[
                { val: 'system', label: 'System', Icon: Monitor },
                { val: 'light', label: 'Light', Icon: Sun },
                { val: 'dark', label: 'Dark', Icon: Moon },
              ].map(({ val, label, Icon }) => (
                <label key={val} className={`tone-option ${form.theme === val ? 'selected' : ''}`} style={{ textAlign: 'center', padding: '16px' }}>
                  <input type="radio" value={val} checked={form.theme === val} onChange={() => handleChange('theme', val)} hidden />
                  <Icon size={24} style={{ marginBottom: 8, color: form.theme === val ? 'var(--accent)' : 'var(--text-muted)' }} />
                  <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{label}</div>
                </label>
              ))}
            </div>
          </div>

          {/* Identity Section */}
          <div className="settings-section card">
            <div className="settings-section-header">
              <h3><User size={18} /> Your Identity</h3>
              <p>Used to personalize AI replies</p>
            </div>
            <div className="settings-fields">
              <div className="form-group">
                <label className="form-label" htmlFor="setting-email">Email Address</label>
                <input id="setting-email" className="form-input" type="email" placeholder="you@freelancer.com" value={form.email} readOnly style={{ opacity: 0.6, cursor: 'not-allowed' }} title="Email is managed by Google sign-in" />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="setting-name">Your Name</label>
                <input id="setting-name" className="form-input" placeholder="Alex Johnson" value={form.name} onChange={e => handleChange('name', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="setting-profession">Your Profession</label>
                <CustomSelect
                  id="setting-profession"
                  value={form.profession}
                  onChange={v => handleChange('profession', v)}
                  placeholder="Select your profession…"
                  options={PROFESSION_OPTIONS}
                />
                <p className="form-hint">Helps the AI give more relevant impact context.</p>
              </div>
            </div>
          </div>

          {/* Billing Section */}
          <div className="settings-section card">
            <div className="settings-section-header">
              <h3><CreditCard size={18} /> Billing Rate</h3>
              <p>Used in out-of-scope reply quotes</p>
            </div>
            <div className="settings-fields settings-fields-row">
              <div className="form-group flex-2">
                <label className="form-label" htmlFor="setting-rate">Hourly Rate</label>
                <div className="rate-input-wrapper">
                  <span className="rate-currency-symbol">{form.currency}</span>
                  <input
                    id="setting-rate"
                    className="form-input rate-input"
                    type="number"
                    min="1"
                    step="1"
                    value={form.hourly_rate}
                    onChange={e => {
                      const v = Number(e.target.value);
                      if (!isNaN(v) && v >= 0) handleChange('hourly_rate', v);
                    }}
                  />
                  <span className="rate-per-hour">/hr</span>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="setting-currency">Currency</label>
                <CustomSelect
                  id="setting-currency"
                  value={form.currency}
                  onChange={v => handleChange('currency', v)}
                  options={CURRENCIES.map(c => ({ value: c, label: c }))}
                />
              </div>
            </div>
          </div>

          {/* Tone Section */}
          <div className={`settings-section card ${!canCustomTone ? 'settings-locked' : ''}`}>
            <div className="settings-section-header">
              <h3><MessageSquare size={18} /> Reply Tone</h3>
              <p>Your default tone for Scope Analysis. You can override it per-check on the Scope Analysis page.</p>
              {!canCustomTone && (
                <div className="settings-lock-badge">
                  <ShieldCheck size={12} />
                  Pro feature — upgrade to customize
                </div>
              )}
            </div>
            <div className="tone-options">
              {TONES.map(tone => (
                <label
                  key={tone.value}
                  className={`tone-option ${form.preferred_tone === tone.value ? 'selected' : ''} ${!canCustomTone && tone.value !== 'professional' ? 'tone-locked' : ''}`}
                >
                  <input
                    type="radio"
                    name="tone"
                    value={tone.value}
                    checked={form.preferred_tone === tone.value}
                    onChange={() => canCustomTone && handleChange('preferred_tone', tone.value)}
                    disabled={!canCustomTone && tone.value !== 'professional'}
                    hidden
                  />
                  <div className="tone-option-header">
                    <span className="tone-option-name">{tone.label}</span>
                    {form.preferred_tone === tone.value && <span className="badge badge-primary">Selected</span>}
                    {!canCustomTone && tone.value !== 'professional' && (
                      <span className="tone-pro-tag">Pro</span>
                    )}
                  </div>
                  <p className="tone-option-desc">{tone.desc}</p>
                </label>
              ))}
            </div>
          </div>

          {/* Document Branding Section */}
          <div className="settings-section card">
            <div className="settings-section-header">
              <h3><Image size={18} /> Document Branding</h3>
              <p>Personalise your Change Order PDFs with your logo and contact details.</p>
            </div>
            <div className="settings-fields">
              <div className="form-group">
                <label className="form-label" htmlFor="branding-company">Company / Studio Name</label>
                <input
                  id="branding-company"
                  className="form-input"
                  placeholder="Smith Design Studio"
                  value={form.branding_company_name}
                  onChange={e => handleChange('branding_company_name', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="branding-logo">Logo URL</label>
                <input
                  id="branding-logo"
                  className="form-input"
                  type="url"
                  placeholder="https://yoursite.com/logo.png"
                  value={form.branding_logo_url}
                  onChange={e => handleChange('branding_logo_url', e.target.value)}
                />
                <p className="form-hint">Paste a publicly accessible image URL (PNG/SVG recommended, max 200px tall).</p>
                {form.branding_logo_url && (
                  <div className="branding-logo-preview">
                    <img
                      src={form.branding_logo_url}
                      alt="Logo preview"
                      onError={e => { e.currentTarget.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="branding-address">Business Address</label>
                <input
                  id="branding-address"
                  className="form-input"
                  placeholder="123 Main St, New York, NY 10001"
                  value={form.branding_address}
                  onChange={e => handleChange('branding_address', e.target.value)}
                />
              </div>
              <div className="settings-fields settings-fields-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="branding-phone"><Phone size={13} style={{ display: 'inline', marginRight: 4 }} />Phone</label>
                  <input
                    id="branding-phone"
                    className="form-input"
                    placeholder="+1 (555) 000-0000"
                    value={form.branding_phone}
                    onChange={e => handleChange('branding_phone', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="branding-website"><Globe size={13} style={{ display: 'inline', marginRight: 4 }} />Website</label>
                  <input
                    id="branding-website"
                    className="form-input"
                    placeholder="https://smithdesign.io"
                    value={form.branding_website}
                    onChange={e => handleChange('branding_website', e.target.value)}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="branding-tagline">Tagline <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.78rem' }}>(optional)</span></label>
                <input
                  id="branding-tagline"
                  className="form-input"
                  placeholder="Design that converts"
                  value={form.branding_tagline}
                  onChange={e => handleChange('branding_tagline', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="settings-save-row">
            <button type="submit" className="btn btn-primary btn-lg" disabled={saving}>
              {saving ? <><div className="spinner" /> Saving…</> : 'Save Settings'}
            </button>
          </div>
        </form>
      )}

      {/* ── Danger Zone ─────────────────────────────────────────── */}
      <div className="danger-zone">
        <div className="danger-zone-header">
          <div>
            <h3>Danger Zone</h3>
            <p>Destructive actions that cannot be undone.</p>
          </div>
        </div>
        <div className="danger-zone-body">
          <div className="danger-zone-row">
            <div>
              <div className="danger-zone-row-title">Delete Account &amp; All Data</div>
              <div className="danger-zone-row-desc">
                Permanently removes your account, all projects, all scope documents, and all evaluation history from our servers. This is irreversible.
              </div>
            </div>
            <button
              className="btn btn-danger"
              onClick={() => { setDeleteInput(''); setShowDeleteModal(true); }}
            >
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {/* ── Delete Confirmation Modal ──────────────────────────── */}
      {showDeleteModal && (
        <div className="modal-overlay animate-fade-in" onClick={() => !deleting && setShowDeleteModal(false)}>
          <div className="delete-modal-panel" onClick={e => e.stopPropagation()}>

            <div className="delete-modal-header">
              <div className="delete-modal-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <div>
                <h2>Delete Account</h2>
                <p>This action is permanent and cannot be undone.</p>
              </div>
              {!deleting && (
                <button className="upload-close-btn" onClick={() => setShowDeleteModal(false)}>
                  <X size={16} />
                </button>
              )}
            </div>

            <div className="delete-modal-body">
              <div className="delete-warning-box">
                <strong>Everything will be permanently deleted:</strong>
                <ul>
                  <li>Your account and login credentials</li>
                  <li>All projects and uploaded scope documents</li>
                  <li>All AI evaluations and evaluation history</li>
                  <li>All settings and preferences</li>
                </ul>
                <p>You will not be able to recover any of this data.</p>
              </div>

              <div className="delete-confirm-field">
                <label htmlFor="delete-confirm-input">
                  Type <strong>DELETE</strong> to confirm:
                </label>
                <input
                  id="delete-confirm-input"
                  type="text"
                  className="form-input delete-confirm-input"
                  placeholder="DELETE"
                  value={deleteInput}
                  onChange={e => setDeleteInput(e.target.value)}
                  disabled={deleting}
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
            </div>

            <div className="delete-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteAccount}
                disabled={deleteInput !== 'DELETE' || deleting}
              >
                {deleting
                  ? <><div className="spinner" /> Deleting everything…</>
                  : 'Permanently Delete My Account'
                }
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
